import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import Select from 'react-select';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { InfoAlert, WarningAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { PlaylistType, TimeBlockType } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import {
  ManageScheduleModal_CustomerFragment,
  ManageScheduleModal_DisplayFragment,
  useDisplayUpdateScheduleMutation,
} from './__generated__/ManageScheduleModal.graphql';

interface Props {
  customer: ManageScheduleModal_CustomerFragment;
  display: ManageScheduleModal_DisplayFragment;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: () => Promise<void> | void;
}

/**
 * Counterpart of `ManagePowerScheduleModal`: same single-display flow, but backed by
 * `displayBulkUpdateSchedule` and the customer's `schedules`.
 */
export function ManageScheduleModal({ display, customer, isOpen, onCancel, onSuccess }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <ManageScheduleModalContent
          customer={customer}
          display={display}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      </ModalContent>
    </Modal>
  );
}

const scheduleOption = selectOptionBaseSchema;

type ScheduleOption = z.TypeOf<typeof scheduleOption>;

const schema = z.object({
  displayId: z.string(),
  schedule: scheduleOption.optional().nullable(),
});

type FormValues = z.infer<typeof schema>;

function ManageScheduleModalContent({
  customer,
  display,
  onCancel,
  onSuccess,
}: {
  customer: ManageScheduleModal_CustomerFragment;
  display: ManageScheduleModal_DisplayFragment;
  onCancel: () => void;
  onSuccess: () => Promise<void> | void;
}) {
  const options = useMemo<ScheduleOption[]>(
    () =>
      customer.schedules.map<ScheduleOption>((s) => ({
        label: s.title,
        value: s.id,
      })),
    [customer],
  );

  const currentSchedule = useMemo(
    () => options.find((o) => o.value === display.schedule?.schedule?.id),
    [display, options],
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      displayId: display.id,
      schedule: currentSchedule,
    },
    resolver: zodResolver(schema),
  });

  const selectedScheduleId = watch('schedule')?.value;
  const selectedSchedule = customer.schedules.find((s) => s.id === selectedScheduleId);

  /**
   * A MIXED playlist only runs on displays that support mixed playlists, so warn as
   * soon as the display is image-only.
   */
  const hasUnsupportedPlaylist =
    !!selectedSchedule?.timeBlocks.some(
      (block) =>
        block.type === TimeBlockType.Playlist && block.data.mediaType === PlaylistType.Mixed,
    ) && display.playlistType === PlaylistType.Image;

  const overridesBookmark = !!selectedSchedule?.timeBlocks.some(
    (block) => block.type === TimeBlockType.Bookmark,
  );

  const analytics = useAnalyticsReporter();
  const toast = useToast();
  const [updateSchedule] = useDisplayUpdateScheduleMutation();
  const performSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await updateSchedule({
          variables: {
            input: {
              displayIds: [values.displayId],
              scheduleId: values.schedule?.value,
            },
          },
        });

        analytics.track('scheduleUpdate');

        await onSuccess();
      } catch (err) {
        toast({
          status: 'error',
          title: t('errorUpdatingSchedule'),
          description: fromError(err, 'DisplayBulkUpdateSchedule'),
        });
      }
    },
    [analytics, updateSchedule, onSuccess, toast],
  );

  const { verifyUserPermissions } = useAuth();

  const isDisplaySettingsUpdateDisabled = !verifyUserPermissions([
    Permission.DisplaySettingsUpdate,
  ]);

  return (
    <>
      <ModalHeader>{t('chooseASchedule')}</ModalHeader>
      <ModalCloseButton />
      <form onSubmit={handleSubmit(performSubmit)}>
        <ModalBody>
          <Stack spacing="4">
            <InfoAlert>{t('schedulePowerOnNotice')}</InfoAlert>
            <InfoAlert>
              <Trans i18nKey="syncScheduleWarn" components={{ strong: <strong /> }} />
            </InfoAlert>
            <FormControl isInvalid={Boolean(errors.schedule)}>
              <FormLabel>{t('chooseSchedule')}</FormLabel>
              <Controller
                name="schedule"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    options={options}
                    components={components}
                    onChange={field.onChange}
                    isLoading={isSubmitting}
                    placeholder={t('clearSchedule')}
                    isMulti={false}
                    escapeClearsValue={false}
                    isClearable={true}
                    isSearchable={true}
                    menuPlacement="auto"
                    isDisabled={isDisplaySettingsUpdateDisabled}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="schedule"
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FormControl>
            {hasUnsupportedPlaylist && (
              <WarningAlert>{t('schedulePlaylistNotSupportedWarn')}</WarningAlert>
            )}
            {overridesBookmark && <WarningAlert>{t('scheduleBookmarkOverrideWarn')}</WarningAlert>}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={onCancel}
            variant="ghost"
            colorScheme="blue"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="solid"
            colorScheme="blue"
            marginLeft="3"
            type="submit"
            isDisabled={isDisplaySettingsUpdateDisabled || isSubmitting}
            isLoading={isSubmitting}
          >
            {t('apply')}
          </Button>
        </ModalFooter>
      </form>
    </>
  );
}

ManageScheduleModal.graphql = {
  fragments: {
    ManageScheduleModal_customer: gql`
      fragment ManageScheduleModal_customer on Customer {
        id
        ...BulkManageSchedule_customer
      }
    `,
    ManageScheduleModal_display: gql`
      fragment ManageScheduleModal_display on Display {
        id
        playlistType
        schedule {
          schedule {
            id
            title
            timeBlocks {
              start
              end
              day
            }
          }
          latestJob {
            id
            __typename
          }
        }
      }
    `,
  },
  mutations: {
    DisplayUpdateSchedule: gql`
      mutation DisplayUpdateSchedule($input: DisplayBulkUpdateScheduleInput!) {
        displayBulkUpdateSchedule(input: $input) {
          displays {
            id
            ...ManageScheduleModal_display
          }
        }
      }
    `,
  },
};
